import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { fetchTruckProfitabilityHistory } from "../../../api/truckProfitabilityService";
import {
  Assumptions,
  calculateForecast,
  defaultAssumptions,
  money,
  monthLabel,
  ProfitabilityHistory,
  recordedCosts,
  recordedRepairCosts,
  requiredFields,
  round,
} from "./forecast";

const fields: {
  key: keyof Assumptions;
  label: string;
  help: string;
  max?: number;
}[] = [
  {
    key: "income",
    label: "Monthly income ($)",
    help: "Planner gross average across at least 3 observed months; held constant in the scenario.",
  },
  {
    key: "miles",
    label: "Monthly mileage (mi)",
    help: "Planner mileage average across at least 3 months; otherwise maintenance odometer history, when available.",
  },
  {
    key: "operatingPerMile",
    label: "Operating costs ($/mi)",
    help: "Enter fuel, driver pay, tolls and other variable costs. Exclude repairs, maintenance, ownership and loan payments.",
  },
  {
    key: "repairsPerMile",
    label: "Repairs and yard costs ($/mi)",
    help: "Recorded costs / planner miles across at least 3 priced months. Other months are excluded, so review this estimate.",
  },
  {
    key: "maintenancePerMile",
    label: "Maintenance budget ($/mi)",
    help: "Manual service budget including upcoming services. Exclude work already budgeted under repairs.",
  },
  {
    key: "ownership",
    label: "Other ownership costs ($/month)",
    help: "Insurance, registration and other fixed costs. Exclude the loan payment, depreciation and costs entered above.",
  },
  {
    key: "loanPayment",
    label: "Loan payment ($/month)",
    help: "Allocated scheduled payment when available. Enter 0 explicitly if debt-free. Excluded from other ownership costs.",
  },
  {
    key: "repairGrowth",
    label: "Monthly repair cost growth (%)",
    help: "0 assumes a flat repair rate. Increase to model deterioration; compounds each month after the first.",
    max: 100,
  },
  {
    key: "resale",
    label: "Current estimated resale value ($)",
    help: "Optional. May default to an active listing's asking price; verify the valuation.",
  },
  {
    key: "resaleDecline",
    label: "Monthly resale decline (%)",
    help: "Optional manual assumption; enter 0 explicitly for a flat resale value.",
    max: 100,
  },
  {
    key: "loanBalance",
    label: "Outstanding loan balance ($)",
    help: "Optional estimated allocated payoff. Enter 0 explicitly if debt-free; excludes sale fees and taxes.",
  },
  {
    key: "loanInterest",
    label: "Annual loan interest (%)",
    help: "Optional. Used with balance and payment to estimate monthly payoff; enter 0 for no interest.",
    max: 100,
  },
];

function ForecastScenario({ history }: { history: ProfitabilityHistory }) {
  const defaults = defaultAssumptions(history);
  const [assumptions, setAssumptions] = useState<Assumptions>(() => defaults);
  const [showActuals, setShowActuals] = useState(true);
  const result = calculateForecast(history, assumptions);
  const recommendation = !result.complete
    ? "Complete assumptions to see a selling recommendation"
    : result.sellAfter === null
      ? "No sale recommended within the next 12 months"
      : result.sellAfter === 0
        ? "Suggested selling period: now, before the forecast starts"
        : `Suggested selling period: end of ${monthLabel(result.rows[result.sellAfter - 1].month)}`;
  let actualCumulative: number | null = 0;
  const actuals = history.months.map((m) => {
    const expenses = recordedCosts(m);
    const net =
      m.income !== null &&
      expenses !== null &&
      !m.missingGrossEntries &&
      !m.unpricedRepairs
        ? round(m.income - expenses)
        : null;
    actualCumulative =
      net !== null && actualCumulative !== null
        ? round(actualCumulative + net)
        : null;
    return { ...m, expenses, net, cumulative: actualCumulative };
  });
  const renderField = (field: (typeof fields)[number]) => {
    const value = assumptions[field.key];
    const required = requiredFields.includes(field.key);
    const invalid =
      value !== null &&
      (!Number.isFinite(value) || value < 0 || value > (field.max ?? 1e9));
    const source =
      value !== defaults[field.key]
        ? "Manual override. "
        : defaults[field.key] === null
          ? "Manual input needed. "
          : field.key === "repairGrowth"
            ? "Scenario default. "
            : "Historical estimate. ";
    return (
      <TextField
        key={field.key}
        label={field.label}
        type="number"
        value={value ?? ""}
        required={required}
        error={invalid}
        fullWidth
        size="small"
        inputProps={{ min: 0, max: field.max ?? 1e9, step: "any" }}
        onChange={(e) =>
          setAssumptions((previous) => ({
            ...previous,
            [field.key]: e.target.value === "" ? null : Number(e.target.value),
          }))
        }
        helperText={
          invalid
            ? `Enter a number from 0 to ${field.max ?? 1e9}.`
            : `${source}${field.help}`
        }
      />
    );
  };
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h2">
          Profitability Forecast
        </Typography>
        <Chip
          size="small"
          color={result.limited ? "warning" : "info"}
          label={
            result.limited
              ? "Limited history • manual scenario"
              : "Historical estimates + manual costs"
          }
        />
        <Typography variant="caption" color="text.secondary">
          Data as of {new Date(history.asOf).toLocaleDateString()}. USD.
        </Typography>
      </Box>
      {result.limited && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Fewer than 3 months have both usable planner income and mileage.
          Supply manual assumptions below; this forecast remains limited.
        </Alert>
      )}
      <Alert
        severity={
          !result.complete
            ? "info"
            : result.sellAfter !== null
              ? "warning"
              : "success"
        }
        sx={{ mb: 2 }}
      >
        <Typography fontWeight={700}>{recommendation}</Typography>
        <Typography variant="body2">{result.explanation}</Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          {result.economic
            ? "Basis: operating profit + resale value − outstanding loan payoff."
            : "Basis: operating profitability only."}{" "}
          Advisory only. This scenario does not mark the truck for sale.
        </Typography>
      </Alert>
      {result.lossStart >= 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Consistently unprofitable from{" "}
          {monthLabel(result.rows[result.lossStart].month)}: at least 3
          consecutive months of projected losses. Those months are highlighted
          below.
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
          mb: 2,
        }}
      >
        {[
          [
            "12-month forecast income",
            result.complete
              ? round(result.rows.reduce((s, r) => s + r.income!, 0))
              : null,
          ],
          [
            "12-month forecast expenses",
            result.complete
              ? round(result.rows.reduce((s, r) => s + r.expenses!, 0))
              : null,
          ],
          ["12-month forecast net profit", result.rows[11].cumulative],
        ].map(([label, value]) => (
          <Paper variant="outlined" sx={{ p: 2 }} key={label as string}>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography
              variant="h5"
              color={
                typeof value === "number" && value < 0
                  ? "error.main"
                  : "text.primary"
              }
            >
              {money(value as number | null)}
            </Typography>
          </Paper>
        ))}
      </Box>
      <Accordion defaultExpanded disableGutters sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Forecast assumptions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Required fields must be completed, including explicit zeroes for
            costs that do not apply. Mileage drives operating, repair and
            maintenance costs; income is a separate monthly assumption. Changes
            recalculate immediately and apply to this view only.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            {fields.slice(0, 8).map(renderField)}
          </Box>
          <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
            Optional resale and loan payoff scenario
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Balance and interest let payments stop at estimated payoff;
            otherwise the scheduled payment stays constant. Complete all four
            fields to compare sale timing using proceeds after payoff.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            {fields.slice(8).map(renderField)}
          </Box>
          <Button
            sx={{ mt: 2 }}
            onClick={() => setAssumptions(defaultAssumptions(history))}
          >
            Reset assumptions
          </Button>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Data sources, missing data and method
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {history.warnings.map((warning) => (
            <Typography variant="body2" key={warning} sx={{ mb: 1 }}>
              {warning}
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Unrecorded operating, maintenance and ownership costs make
            historical actuals partial. Accepted operating requests are a
            partial cost subtotal, so a full operating cost assumption is
            required. Missing values display as Unknown. Historical cumulative
            profit becomes unknown after any gap; forecast cumulative profit
            starts from zero at the forecast boundary.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Historical averages use only observed months and require at least 3
            usable months per input. Repair rates use priced months only and may
            overstate or understate typical costs. No automatic age penalty is
            assumed; use repair growth to model deterioration.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Net profit here is gross less modeled cash costs, including loan
            payments, before tax and depreciation. It is a planning measure.
            Resale comparison maximizes cumulative forecast profit plus resale
            less loan payoff at monthly boundaries, including selling now.
            Prices decline at the entered monthly rate; interest accrues
            monthly.
          </Typography>
          <Typography variant="body2">
            Current odometer:{" "}
            {history.currentMileage === null
              ? "Unknown — mileage-based service dates cannot be estimated"
              : `${history.currentMileage.toLocaleString()} mi`}
            . {history.maintenance.length} maintenance operation(s) available.
            Future due-mileage crossings and due dates use the entered monthly
            mileage; recurring service intervals are not inferred.
          </Typography>
          {history.maintenance.map((m, i) => (
            <Typography variant="body2" key={i} sx={{ mt: 1 }}>
              {m.operation}: due date{" "}
              {m.dueDate ? new Date(m.dueDate).toLocaleDateString() : "Unknown"}
              ; due mileage{" "}
              {m.dueMileage === null
                ? "Unknown"
                : `${m.dueMileage.toLocaleString()} mi`}
              . Price unknown; covered by your maintenance budget.
            </Typography>
          ))}
        </AccordionDetails>
      </Accordion>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          gap: 2,
        }}
      >
        <Typography variant="h6" component="h3">
          Monthly actuals and 12-month forecast
        </Typography>
        <Button onClick={() => setShowActuals((value) => !value)}>
          {showActuals ? "Hide actuals" : "Show actuals"}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Actuals are planner gross, accepted operating requests and final repair
        costs. Historical net profit excludes unrecorded costs. Estimates use
        the assumptions above. Scroll horizontally for the cost breakdown.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table
          size="small"
          sx={{ minWidth: 1450 }}
          aria-label="Truck monthly profitability"
        >
          <TableHead>
            <TableRow>
              {[
                "Month",
                "Basis",
                "Income",
                "Expenses",
                "Net profit",
                "Cumulative profit",
                "Mileage",
                "Operating",
                "Repairs / yard",
                "Maintenance",
                "Ownership",
                "Loan payment",
                "Maintenance due",
              ].map((label) => (
                <TableCell key={label} sx={{ fontWeight: 700 }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {showActuals &&
              actuals.map((m) => (
                <TableRow key={m.month} sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {monthLabel(m.month)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Partial actual"
                    />
                  </TableCell>
                  <TableCell>
                    {money(m.income)}
                    {m.missingGrossEntries > 0 && " (incomplete)"}
                  </TableCell>
                  <TableCell>
                    {money(m.expenses)}
                    {m.unpricedRepairs > 0 && " (unpriced repairs)"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color:
                        m.net !== null && m.net < 0 ? "error.main" : undefined,
                    }}
                  >
                    {money(m.net)}
                  </TableCell>
                  <TableCell>{money(m.cumulative)}</TableCell>
                  <TableCell>
                    {m.mileage === null
                      ? "Unknown"
                      : m.mileage.toLocaleString()}
                    {m.missingMileageEntries > 0 && " (incomplete)"}
                  </TableCell>
                  <TableCell>{money(m.operatingCosts)}</TableCell>
                  <TableCell>{money(recordedRepairCosts(m))}</TableCell>
                  <TableCell>Unknown</TableCell>
                  <TableCell>Unknown</TableCell>
                  <TableCell>Unknown</TableCell>
                  <TableCell>—</TableCell>
                </TableRow>
              ))}
            {result.rows.map((r) => (
              <TableRow
                key={r.month}
                sx={{
                  bgcolor: r.consistentlyUnprofitable
                    ? "error.light"
                    : undefined,
                  "& td": { borderTopStyle: "dashed" },
                }}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {monthLabel(r.month)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={
                      r.consistentlyUnprofitable
                        ? "Estimate • losses"
                        : "Estimate"
                    }
                  />
                </TableCell>
                <TableCell>{money(r.income)}</TableCell>
                <TableCell>{money(r.expenses)}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{money(r.net)}</TableCell>
                <TableCell>{money(r.cumulative)}</TableCell>
                <TableCell>
                  {r.miles === null ? "Unknown" : r.miles.toLocaleString()}
                </TableCell>
                <TableCell>{money(r.operating)}</TableCell>
                <TableCell>{money(r.repairs)}</TableCell>
                <TableCell>{money(r.maintenance)}</TableCell>
                <TableCell>{money(r.ownership)}</TableCell>
                <TableCell>{money(r.loanPayment)}</TableCell>
                <TableCell>
                  {r.maintenanceDue.length ? (
                    <Tooltip title="Included in your maintenance budget; verify service quotes.">
                      <span>{r.maintenanceDue.join(", ")}</span>
                    </Tooltip>
                  ) : (
                    "No known due service"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function ProfitabilityForecast({
  truckId,
}: {
  truckId: string;
}) {
  const { token } = useAuth();
  const query = useQuery({
    queryKey: ["truckProfitability", truckId],
    queryFn: () => fetchTruckProfitabilityHistory(token, truckId),
    enabled: !!truckId,
    refetchOnWindowFocus: false,
  });
  if (query.isLoading)
    return (
      <Box role="status" sx={{ p: 3 }}>
        <CircularProgress size={24} /> Loading profitability history…
      </Box>
    );
  if (query.isError)
    return (
      <Alert
        severity="error"
        action={<Button onClick={() => query.refetch()}>Retry</Button>}
      >
        Profitability history could not be loaded. No forecast can be calculated
        from unavailable data.
      </Alert>
    );
  if (!query.data) return null;
  return (
    <Box sx={{ my: 3 }}>
      <ForecastScenario key={truckId} history={query.data} />
    </Box>
  );
}
